import { BRAND } from "@/lib/brand";
// Shared JSON-LD helpers for the English section (Smart Sustainability).
// Keep the Person as the primary entity and reference it by @id from other
// schemas so search engines / LLMs merge the graph correctly.

export const SITE_URL = "https://smartsustainability.ai";
export const TR_SITE_URL = "https://akillisurdurulebilirlik.com";
export const PERSON_ID = `${SITE_URL}/#person`;
export const WEBSITE_ID = `${SITE_URL}/#website`;
export const EN_SITE_NAME = BRAND.en.siteName;
export const EN_HOME = `${SITE_URL}/`;

// Verified public profiles for Arda Öztaşkın.
export const AUTHOR_SAME_AS = [
  "https://www.linkedin.com/in/ardaoztaskin/",
  "https://www.amazon.com/stores/Arda-%C3%96zta%C5%9Fk%C4%B1n/author/B0H596GS9Q",
  "https://www.ekonomigazetesi.com/yazar/arda-oztaskin-34322",
  "https://akillisurdurulebilirlik.com/yazar",
];

export const AUTHOR_KNOWS_ABOUT = [
  "Artificial intelligence",
  "Sustainability",
  "Climate change",
  "Human judgment",
  "Institutional trust",
  "Epistemic infrastructure",
  "Technology and society",
];

// Canonical books, used inside Person.author (authorOf semantics).
export const AUTHOR_BOOKS = [
  {
    name: "The Loneliness Economy",
    alternateName: "Yalnızlık A.Ş.",
    datePublished: "2026",
    sameAs: "https://www.amazon.com/dp/B0H59HPG62",
    inLanguage: "tr",
  },
  {
    name: "Artificial Intelligence: The Two Faces of Power",
    alternateName: "Yapay Zekâ: Gücün İki Yüzü",
    datePublished: "2024",
    sameAs: "https://www.amazon.com/dp/B0H58HHS3B",
    inLanguage: "tr",
  },
  {
    name: "It's Not a Climate Crisis, It's a Human Crisis",
    alternateName: "İklim Değil, İnsanlık Krizi",
    datePublished: "2023",
    sameAs: "https://www.amazon.com/dp/B0H598BYTV",
    inLanguage: "tr",
  },
] as const;

/**
 * Full Person schema for Arda Öztaşkın. This is the primary entity of
 * the site — Article/DefinedTerm/CreativeWork schemas reference it by
 * @id rather than embedding a duplicate Person object.
 */
export function personSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": PERSON_ID,
    name: "Arda Öztaşkın",
    alternateName: "Arda Oztaskin",
    jobTitle: "Writer",
    description:
      "Independent writer developing original essays, concepts, and frameworks on artificial intelligence, sustainability, institutional trust, and the future of human judgment.",
    url: EN_HOME,
    image: `${SITE_URL}/og-image.jpg`,
    knowsAbout: AUTHOR_KNOWS_ABOUT,
    sameAs: AUTHOR_SAME_AS,
    author: AUTHOR_BOOKS.map((b) => ({
      "@type": "Book",
      name: b.name,
      alternateName: b.alternateName,
      datePublished: b.datePublished,
      inLanguage: b.inLanguage,
      sameAs: b.sameAs,
      author: { "@id": PERSON_ID },
    })),
  };
}

/**
 * WebSite schema for the English publication. The site is presented as
 * Arda Öztaşkın's intellectual publication, not as an organization.
 */
export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    name: EN_SITE_NAME,
    alternateName: BRAND.en.altSiteName,
    url: SITE_URL,
    inLanguage: ["en", "tr"],
    author: { "@id": PERSON_ID },
    publisher: { "@id": PERSON_ID },
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

export type Crumb = { name: string; item: string };

export function breadcrumbSchema(items: Crumb[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.item,
    })),
  };
}

// Normalise: keep a bare domain root as "https://host/", strip trailing
// slashes from deeper paths so canonical and hreflang always agree.
const stripSlash = (u: string) => {
  try {
    const url = new URL(u);
    const path = url.pathname === "/" ? "/" : url.pathname.replace(/\/+$/, "");
    return `${url.origin}${path}`;
  } catch {
    return u;
  }
};

/**
 * Build hreflang <link> entries for an EN leaf. If a Turkish counterpart
 * exists, pass its absolute URL so search engines pair the two localized
 * URLs and x-default points at the Turkish one. With no counterpart, only
 * the English hreflang is emitted and x-default self-references it —
 * never a URL that 404s or canonicalises elsewhere.
 */
export function enHreflangLinks(enUrl: string, trUrl?: string) {
  const en = stripSlash(enUrl);
  const tr = trUrl ? stripSlash(trUrl) : undefined;
  const links: Array<{ rel: string; hreflang: string; href: string }> = [
    { rel: "alternate", hreflang: "en", href: en },
  ];
  if (tr) links.push({ rel: "alternate", hreflang: "tr", href: tr });
  links.push({ rel: "alternate", hreflang: "x-default", href: tr ?? en });
  return links;
}

export { TR_SITE_URL as TR_SITE };
